// src/admin/pages/RunDetailPage.jsx
//
// Run detail page, mounted at /runs/:taskId, /admin/runs/:taskId and /tenant-admin/runs/:taskId.
// It is reachable by every role, but in practice it is a superadmin page: members and workspace
// admins open the RunDetailModal quick-look from their lists and reach this page only by URL.
// Meta card, error / warnings, the brief (BriefPreview) and the result, drawn by the same
// renderResultEntry the generator uses from a history-shaped synthEntry. The warnings
// (components/warnings) and the «Affermazioni nel testo» / «Fonti consultate» panels
// (components/claims) are the generator's own, so a run reads the same here and in the run
// card. The raw stored-result JSON disclosures are superadmin-only (support / debugging).
//
// The «Risultato» heading carries the generator's «Scarica» action: the shared
// DownloadMenu (components/outputs/DownloadMenu.jsx) lists
// downloadOptionsForEntry(synthEntry) and a row builds the file with
// buildDownload(synthEntry, id) and saves it through downloadBlob (both
// src/publish/downloads.js). The menu is offered only while the result is
// available and the catalogue has a row; a build that rejects saves nothing
// and shows nothing (never server text). Nothing downloads before a click.
// A Blog SEO run draws its own screen (components/outputs/blogSeo/), whose card
// carries «Scarica tutto» (the same catalogue): for that format the heading has
// no «Scarica».
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink, AlertCircle, Copy, Check, Download, ChevronDown } from 'lucide-react';
import { getRunDetail } from '../runsApi.js';
import DownloadMenu from '../../components/outputs/DownloadMenu.jsx';
import { downloadOptionsForEntry, buildDownload, downloadBlob } from '../../publish/downloads.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import JsonViewer from '../components/JsonViewer.jsx';
import Disclosure from '../components/Disclosure.jsx';
import BriefPreview from '../../components/ui/BriefPreview.jsx';
import renderResultEntry from '../../components/outputs/renderResultEntry.jsx';
import { isBlogSeoEntry } from '../../components/outputs/blogSeo/BlogSeoResultEntry.jsx';
import WarningList from '../../components/warnings/WarningList.jsx';
import ClaimsPanel from '../../components/claims/ClaimsPanel.jsx';
import SourcesPanel from '../../components/claims/SourcesPanel.jsx';
import { useClipboard } from '../../hooks/useClipboard.js';
import { briefEnvelopeToFormState } from '../../utils/briefEnvelopeToFormState.js';
import { resultsFromRunDetail } from '../../utils/runResults.js';
import { useTonePresets } from '../../hooks/useContextOptions.js';
import { getModeConfig, ESC_ACCENT } from '../../constants/modes.js';
import { COLORS, FONT_MONO, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

// Light theme tokens — match every key the per-platform renderers consume
// (mirror of EscContentCreatorPro.jsx:464-467 light branch with renderer-required extras).
const LIGHT_T = {
  bg:          COLORS.surface,
  surface:     'rgba(0,0,0,0.015)',
  inputBg:     COLORS.surface,
  inputBorder: COLORS.borderStrong,
  border:      COLORS.borderStrong,
  text:        COLORS.ink,
  textSoft:    COLORS.ink,
  textMuted:   COLORS.ink,
  textFaint:   COLORS.textSecondary,
  textDim:     COLORS.textSecondary,
  btnText:     COLORS.surface,
};
// Opacity helper used by renderers as `background: w(0.03)` etc.
// MUST be a function; replicates the light-mode branch of EscContentCreatorPro's `w`.
const LIGHT_W = (opacity) =>
  `rgba(0,0,0,${Math.min(opacity * 2.5, 0.2).toFixed(2)})`;

const cardStyle = {
  background: COLORS.surface, borderRadius: RADII.control,
  border: `1px solid ${COLORS.border}`, padding: '20px',
  marginBottom: '20px',
};
const btnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '6px 12px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
  color: COLORS.ink, fontSize: '12px', cursor: 'pointer',
  fontFamily: FONT_SANS, textDecoration: 'none',
};
const primaryBtnStyle = {
  ...btnStyle,
  background: ESC_ACCENT, color: COLORS.surface, border: 'none', fontWeight: 600,
};

function Meta({ label, value }) {
  return (
    <div style={{ minWidth: '160px' }}>
      <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: COLORS.ink, marginTop: '2px', wordBreak: 'break-word' }}>{value || '—'}</div>
    </div>
  );
}

export default function RunDetailPage() {
  const { t, i18n } = useTranslation('admin');
  const language = i18n.language;
  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperadmin } = useAuth();

  // Tone labels are per-tenant. For a member / tenant-admin the active tenant IS
  // the run's tenant, so the served labels are the right ones and match what the
  // form and Storico Generazioni show. For a superadmin they would be the WRONG
  // tenant's labels, so the fetch is skipped and BriefPreview falls back to i18n.
  const tonePresets = useTonePresets(!isSuperadmin);

  const emptyResultExplanation = {
    failed:      t('runDetail.emptyExplanation.failed'),
    in_progress: t('runDetail.emptyExplanation.in_progress'),
    pending:     t('runDetail.emptyExplanation.pending'),
    cancelled:   t('runDetail.emptyExplanation.cancelled'),
    canceled:    t('runDetail.emptyExplanation.canceled'),
  };

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [copiedError, setCopiedError] = useState(false);
  // Drives the "Copia/Copiato" UI of the rendered result: PiecesResultEntry
  // (components/outputs/) hands copiedId / onCopy to its pieces — ReadyToPublish,
  // every PieceRow. Without real handlers those copy
  // buttons silently no-op and look broken to the user.
  // ⚠️ Every piece calls `onCopy(text, id, opts)` — the local handler this
  // replaced took `(id, text)`, so on this page a click copied the BUTTON ID
  // and lit no "Copiato". useClipboard is the wizard's own hook, rich copy
  // (text/html) included.
  const [copiedId, setCopiedId] = useState(null);
  const { copy: onCopy } = useClipboard({
    onSuccess: (id) => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(curr => (curr === id ? null : curr)), 1500);
    },
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getRunDetail(taskId)
      .then(d => { if (alive) setDetail(d); })
      .catch(err => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [taskId]);

  async function handleCopyErrorDetails() {
    if (!detail) return;
    const details = {
      task_id: detail.task_id, status: detail.status,
      error_code: detail.error_code, error_message: detail.error_message,
      created_at: detail.created_at, completed_at: detail.completed_at,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(details, null, 2));
      setCopiedError(true);
      setTimeout(() => setCopiedError(false), 1500);
    } catch { /* clipboard unavailable */ }
  }

  // Build synthetic history entry for both: the in-page Result renderer AND
  // the cross-route reload payload. Its `results` come from the one shared
  // mapping (utils/runResults.js :: resultsFromRunDetail), the same shape the
  // generator builds for a live run.
  const synthEntry = useMemo(() => {
    if (!detail || !detail.result_json) return null;
    // Derive activeMode from the BE-saved brief (mirrors briefEnvelopeToFormState).
    const fe = detail.brief_json ? briefEnvelopeToFormState(detail.brief_json) : {};
    const activeMode = fe.activeMode || detail.platform;
    return {
      id: detail.task_id,
      timestamp: detail.completed_at ? new Date(detail.completed_at) : new Date(detail.created_at),
      mode: activeMode,
      modeConfig: getModeConfig(activeMode),
      results: resultsFromRunDetail(detail),
      params: detail.brief_json
        ? { ...detail.brief_json, activeMode, socialChannel: fe.socialChannel, emailChannel: fe.emailChannel }
        : null,
      generationDuration: null,
    };
  }, [detail]);

  // «Scarica»: the catalogue rows of the synth entry (none without an entry) and
  // the open state of the shared menu. A rejected build is swallowed on purpose:
  // the reason is a code or a chunk that did not load, and the page has nothing
  // honest to say about it.
  const [dlOpen, setDlOpen] = useState(false);
  const dlRef = useRef(null);
  const closeDl = useCallback(() => setDlOpen(false), []);
  // The rows' labels (and the piece list behind them) follow the UI language.
  const downloadOptions = useMemo(
    () => (synthEntry ? downloadOptionsForEntry(synthEntry) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [synthEntry, language],
  );
  const handleDownload = useCallback((id) => {
    setDlOpen(false);
    if (!synthEntry) return;
    buildDownload(synthEntry, id)
      .then(({ blob, filename }) => downloadBlob(blob, filename))
      .catch(() => {});
  }, [synthEntry]);

  const langfuseUrl = isSuperadmin && detail?.langfuse_url ? detail.langfuse_url : null;
  // Derive the list path by dropping the trailing /:taskId segment so the page
  // works under every mount: /admin/runs, /tenant-admin/runs, and the
  // member-facing /runs. location.state.from still overrides when present.
  const defaultBack = location.pathname.replace(/\/[^/]+$/, '') || '/runs';
  const backTo = (location.state && location.state.from) || defaultBack;

  function handleBack() { navigate(backTo); }

  function handleReloadIntoGenerator() {
    if (!detail?.brief_json) return;
    navigate('/content-generator', {
      state: {
        preloadedBrief: detail.brief_json,
        preloadedHistoryEntry: synthEntry,
      },
    });
  }

  if (loading) return <div style={{ padding: '40px', color: COLORS.textSecondary, fontFamily: FONT_SANS }}>{t('shared.loading')}</div>;
  if (error)   return <div style={{ padding: '40px', color: SEMANTIC.errore.fg, fontFamily: FONT_SANS }}>{error}</div>;
  if (!detail) return <div style={{ padding: '40px', color: COLORS.textSecondary, fontFamily: FONT_SANS }}>{t('runDetail.notFound')}</div>;

  const result = detail.result_json;
  const resultAvailable = !!result && (detail.status === 'completed' || detail.status === 'approved');
  const warnings = result && Array.isArray(result.warnings) ? result.warnings : [];
  // Members and tenant admins only: for a superadmin the brief is another
  // tenant's, so loading it into their own Content Generator would be wrong.
  const showReloadBtn = !isSuperadmin && !!detail.brief_json;

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1100px' }}>
      <button onClick={handleBack} style={{ ...btnStyle, marginBottom: '20px', background: 'transparent', border: 'none' }}>
        <ArrowLeft size={15} /> {t('shared.back')}
      </button>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, margin: 0, wordBreak: 'break-word' }}>
            {detail.topic || t('runDetail.untitled')}
          </h1>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
            <StatusBadge status={detail.status} />
            <code style={{ fontFamily: FONT_MONO, fontSize: '11px', color: COLORS.textSecondary }}>
              {detail.task_id}
            </code>
          </div>
        </div>
        {isSuperadmin && (
          langfuseUrl ? (
            <a href={langfuseUrl} target="_blank" rel="noopener noreferrer" style={btnStyle}>
              <ExternalLink size={13} /> {t('runDetail.langfuse')}
            </a>
          ) : (
            <span style={{ ...btnStyle, opacity: 0.5, cursor: 'not-allowed' }} title={t('runDetail.linkUnavailable')} aria-disabled="true">
              <ExternalLink size={13} /> {t('runDetail.langfuse')}
            </span>
          )
        )}
      </div>

      {/* Meta card */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
          <Meta label={t('runDetail.meta.tenant')}      value={detail.tenant_id} />
          <Meta label={t('runDetail.meta.platform')}    value={`${detail.platform || '—'}${detail.content_format ? ' / ' + detail.content_format : ''}`} />
          <Meta label={t('runDetail.meta.objective')}   value={detail.objective} />
          <Meta label={t('runDetail.meta.persona')}     value={detail.persona} />
          <Meta label={t('runDetail.meta.created')}     value={detail.created_at   ? new Date(detail.created_at).toLocaleString('it-IT') : null} />
          <Meta label={t('runDetail.meta.completed')}   value={detail.completed_at ? new Date(detail.completed_at).toLocaleString('it-IT') : null} />
          <Meta label={t('runDetail.meta.reviewed')}    value={detail.reviewed_at  ? new Date(detail.reviewed_at).toLocaleString('it-IT') : null} />
          <Meta label={t('runDetail.meta.iterations')}  value={detail.iteration_count != null ? String(detail.iteration_count) : null} />
          <Meta label={t('runDetail.meta.criticScore')} value={detail.critic_score != null ? String(detail.critic_score) : null} />
          <Meta label={t('runDetail.meta.styleScore')}  value={detail.style_score  != null ? String(detail.style_score)  : null} />
          <Meta label={t('runDetail.meta.factCheck')}   value={detail.fact_check_score != null ? String(detail.fact_check_score) : null} />
          <Meta label={t('runDetail.meta.createdBy')}   value={detail.created_by_email} />
          <Meta label={t('runDetail.meta.reviewedBy')}  value={detail.reviewed_by_email} />
        </div>
      </div>

      {/* Error card */}
      {detail.status === 'failed' && (detail.error_message) && (
        <div style={{ ...cardStyle, background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.bg}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: SEMANTIC.errore.fg, fontWeight: 600, fontSize: '13px' }}>
              <AlertCircle size={15} /> {t('runDetail.error')}
            </div>
            <button
              type="button"
              onClick={handleCopyErrorDetails}
              title={t('runDetail.copyErrorTitle')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '4px 8px', borderRadius: RADII.controlInner,
                border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
                color: copiedError ? SEMANTIC.verde.fg : COLORS.ink,
                fontSize: '11px', cursor: 'pointer',
                fontFamily: FONT_SANS,
              }}
            >
              {copiedError ? <Check size={12} /> : <Copy size={12} />}
              {copiedError ? t('shared.copied') : t('runDetail.copyErrorDetails')}
            </button>
          </div>
          <div style={{ fontSize: '13px', color: COLORS.ink }}>{detail.error_message}</div>
          {detail.error_code && (
            <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '6px', fontFamily: FONT_MONO }}>
              {detail.error_code}
            </div>
          )}
        </div>
      )}

      {/* Warnings — our words, coloured by severity (components/warnings); ⛔ never w.message */}
      {warnings.length > 0 && (
        <div style={cardStyle}>
          <WarningList warnings={warnings} t={LIGHT_T} />
        </div>
      )}

      {/* Input parameters */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{t('runDetail.inputParams')}</h3>
          {showReloadBtn && (
            <button
              type="button"
              onClick={handleReloadIntoGenerator}
              style={primaryBtnStyle}
              title={t('runDetail.reloadTitle')}
            >
              <Download size={13} /> {t('runDetail.reload')}
            </button>
          )}
        </div>
        {detail.brief_json
          ? <BriefPreview brief={detail.brief_json} t={LIGHT_T} color={ESC_ACCENT} tonePresets={tonePresets} />
          : <div style={{ padding: '16px', color: COLORS.textSecondary, fontSize: '13px', fontStyle: 'italic' }}>
              {t('runDetail.inputUnavailable')}
            </div>}
      </div>

      {/* Result */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{t('runDetail.resultHeading')}</h3>
          {resultAvailable && synthEntry && downloadOptions.length > 0 && !isBlogSeoEntry(synthEntry) && (
            <div ref={dlRef} style={{ position: 'relative' }}>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={dlOpen}
                onClick={() => setDlOpen(o => !o)}
                style={{ ...btnStyle, fontWeight: 600 }}
              >
                <Download size={13} /> {t('runDetail.download')} <ChevronDown size={11} />
              </button>
              {dlOpen && (
                <DownloadMenu
                  options={downloadOptions}
                  onSelect={handleDownload}
                  onClose={closeDl}
                  containerRef={dlRef}
                  t={LIGHT_T}
                />
              )}
            </div>
          )}
        </div>
        {resultAvailable && synthEntry
          ? renderResultEntry(synthEntry, {
              t: LIGHT_T,
              w: LIGHT_W,                       // callable — see Design > Light theme constants
              copiedId,                         // local state — wires copy buttons inside renderers
              onCopy,                           // local handler — see useState above
            })
          : <div style={{ padding: '16px', color: COLORS.textSecondary, fontSize: '13px', lineHeight: 1.5 }}>
              <div style={{ fontStyle: 'italic' }}>
                {t('runDetail.resultUnavailable', { status: t(`runDetail.statusLabel.${detail.status}`, { defaultValue: detail.status }) })}
              </div>
              {emptyResultExplanation[detail.status] && (
                <div style={{ marginTop: '6px' }}>{emptyResultExplanation[detail.status]}</div>
              )}
            </div>}
      </div>

      {/* Affermazioni nel testo + Fonti consultate — the same panels as the generator run card */}
      {resultAvailable && synthEntry?.results.verification && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <ClaimsPanel key={synthEntry.results._taskId} results={synthEntry.results} t={LIGHT_T} w={LIGHT_W} />
            <SourcesPanel key={`${synthEntry.results._taskId}-sources`} results={synthEntry.results} t={LIGHT_T} w={LIGHT_W} />
          </div>
        </div>
      )}

      {/* Raw stored result — superadmin only (support / debugging). A run stored before
          claims and sources v1 still carries reviews / sources here. */}
      {isSuperadmin && result && result.drafts && (
        <div style={cardStyle}>
          <Disclosure title={t('runDetail.disclosure.drafts')} defaultOpen={false}>
            <JsonViewer value={result.drafts} />
          </Disclosure>
        </div>
      )}
      {isSuperadmin && result && result.reviews && (
        <div style={cardStyle}>
          <Disclosure title={t('runDetail.disclosure.reviews')} defaultOpen={false}>
            <JsonViewer value={result.reviews} />
          </Disclosure>
        </div>
      )}
      {isSuperadmin && result && result.style && (
        <div style={cardStyle}>
          <Disclosure title={t('runDetail.disclosure.style')} defaultOpen={false}>
            <JsonViewer value={result.style} />
          </Disclosure>
        </div>
      )}
      {isSuperadmin && result && result.research && (
        <div style={cardStyle}>
          <Disclosure title={t('runDetail.disclosure.research')} defaultOpen={false}>
            <JsonViewer value={result.research} />
          </Disclosure>
        </div>
      )}
      {isSuperadmin && result && result.sources && (
        <div style={cardStyle}>
          <Disclosure title={t('runDetail.disclosure.sources')} defaultOpen={false}>
            <JsonViewer value={result.sources} />
          </Disclosure>
        </div>
      )}
      {isSuperadmin && result && result.verification && (
        <div style={cardStyle}>
          <Disclosure title={t('runDetail.disclosure.verification')} defaultOpen={false}>
            <JsonViewer value={result.verification} />
          </Disclosure>
        </div>
      )}
      {isSuperadmin && result && result.sources_consulted && (
        <div style={cardStyle}>
          <Disclosure title={t('runDetail.disclosure.sourcesConsulted')} defaultOpen={false}>
            <JsonViewer value={result.sources_consulted} />
          </Disclosure>
        </div>
      )}
    </div>
  );
}
